import React, { useEffect, useMemo } from 'react'
import { Link, useParams, useLocation } from 'react-router-dom'
import { useTranslation, useCurrentLanguage } from '../hooks/useTranslation'
import { useLanguage } from '../contexts/LanguageContext'
import { usePageSeo } from '../hooks/usePageSeo'
import { absoluteUrl, getOrganizationSchemaId } from '../lib/siteUrl'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'

function TeamMemberNotFound ({ langPrefix, t }) {
  useEffect(() => {
    document.title = `${t('home.teamDetail.notFound.title')} | VIVA Psychotherapy`
    const upsert = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`)
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute('name', name)
        document.head.appendChild(el)
      }
      el.setAttribute('content', content)
    }
    upsert('robots', 'noindex, nofollow')
    upsert('googlebot', 'noindex, nofollow')
  }, [t])

  return (
    <div className="min-h-screen bg-primary-offWhite">
      <main className="py-16 px-4 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('home.teamDetail.notFound.title')}</h1>
        <p className="text-gray-600 mb-8">{t('home.teamDetail.notFound.body')}</p>
        <Link
          to={`${langPrefix}/team`}
          className="inline-flex items-center text-primary-darkGreen font-semibold hover:underline"
        >
          {t('home.teamDetail.notFound.back')}
        </Link>
      </main>
    </div>
  )
}

const TeamDetail = () => {
  const { slug } = useParams()
  const location = useLocation()
  const t = useTranslation()
  const { isLoading } = useLanguage()
  const language = useCurrentLanguage()
  const langPrefix = language === 'pt' ? '/pt' : language === 'en' ? '/en' : ''

  const members = !isLoading ? t('home.team.members') || [] : []
  const member = members.find((m) => m.slug === slug)

  const metaDescription = useMemo(() => {
    if (!member?.bio) return t('home.seo.team.description')
    const oneLine = String(member.bio).replace(/\s+/g, ' ').trim()
    return oneLine.length <= 160 ? oneLine : `${oneLine.slice(0, 157)}…`
  }, [member, t])

  usePageSeo({
    enabled: !isLoading && !!member,
    title: member ? `${member.name} | VIVA Psychotherapy` : t('home.seo.team.title'),
    description: metaDescription,
    keywords: t('home.seo.team.keywords'),
    pathname: location.pathname
  })

  useEffect(() => {
    if (isLoading || !member) {
      document.querySelector('script[type="application/ld+json"][data-team-person]')?.remove()
      return undefined
    }
    const pageUrl = absoluteUrl(location.pathname)
    const imageSrc = member.image
      ? member.image.startsWith('http')
        ? member.image
        : absoluteUrl(member.image.startsWith('/') ? member.image : `/${member.image}`)
      : undefined
    const doc = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      '@id': `${pageUrl}#person`,
      name: member.name,
      jobTitle: member.title || undefined,
      description: String(member.bio || '').slice(0, 2000),
      url: pageUrl,
      worksFor: { '@id': getOrganizationSchemaId() },
      ...(imageSrc ? { image: imageSrc } : {})
    }
    let el = document.querySelector('script[type="application/ld+json"][data-team-person]')
    if (!el) {
      el = document.createElement('script')
      el.setAttribute('type', 'application/ld+json')
      el.setAttribute('data-team-person', '')
      document.head.appendChild(el)
    }
    el.textContent = JSON.stringify(doc)
    return () => {
      document.querySelector('script[type="application/ld+json"][data-team-person]')?.remove()
    }
  }, [isLoading, member, location.pathname])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-offWhite">
        <main>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded mb-4" />
              <div className="h-4 bg-gray-300 rounded mb-2" />
              <div className="h-4 bg-gray-300 rounded mb-2" />
              <div className="h-4 bg-gray-300 rounded w-3/4" />
            </div>
          </div>
        </main>
      </div>
    )
  }

  if (!member) {
    return (
      <TeamMemberNotFound langPrefix={langPrefix} t={t} />
    )
  }

  const bioParagraphs = String(member.bio || '')
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean)

  const showExtended = member.detailExtended === true

  const credentialEducationItems =
    member.educationItems !== undefined ? member.educationItems : t('about.meetTherapist.education.items')
  const showAffiliationsAccordion = member.showAffiliationsAccordion !== false
  const credentialAffiliationsItems =
    member.affiliationsItems !== undefined
      ? member.affiliationsItems
      : t('about.meetTherapist.affiliations.items')
  const showLicensesAccordion = member.showLicensesAccordion !== false
  const credentialLicensesItems = showLicensesAccordion
    ? member.licensesItems !== undefined
      ? member.licensesItems
      : t('about.meetTherapist.licenses.items')
    : []

  const showLanguagesAccordion = member.showLanguagesAccordion === true
  const credentialLanguagesItems = showLanguagesAccordion
    ? member.languagesItems !== undefined
      ? member.languagesItems
      : t('about.meetTherapist.languages.items')
    : []

  const credentialAccordions = [
    {
      id: `team-${member.slug}-education`,
      title: t('about.meetTherapist.education.title'),
      items: credentialEducationItems || [],
      show: true,
      preLine: false,
    },
    {
      id: `team-${member.slug}-affiliations`,
      title: t('about.meetTherapist.affiliations.title'),
      items: credentialAffiliationsItems || [],
      show: showAffiliationsAccordion,
      preLine: false,
    },
    {
      id: `team-${member.slug}-licenses`,
      title: t('about.meetTherapist.licenses.title'),
      items: credentialLicensesItems || [],
      show: showLicensesAccordion,
      preLine: true,
    },
    {
      id: `team-${member.slug}-languages`,
      title: t('about.meetTherapist.languages.title'),
      items: credentialLanguagesItems || [],
      show: showLanguagesAccordion,
      preLine: false,
    },
  ].filter((section) => section.show && section.items.length > 0)

  const profileGridClass = [
    'grid grid-cols-1 gap-y-8 sm:gap-y-10 lg:grid-cols-2 lg:gap-x-12 lg:gap-y-0 lg:items-start',
    showExtended
      ? "[grid-template-areas:'photo'_'bio'_'creds'] lg:[grid-template-areas:'bio_stack'_'bio_stack']"
      : "[grid-template-areas:'photo'_'bio'] lg:[grid-template-areas:'bio_stack'_'bio_stack']",
  ].join(' ')

  return (
    <div className="min-h-screen bg-white">
      <main>
        <section className="py-8 sm:py-12 lg:py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <Link
                to={`${langPrefix}/team`}
                className="inline-flex items-center text-sm font-medium text-primary-darkGreen hover:underline"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('home.team.title')}
              </Link>
            </div>

            <div className={profileGridClass}>
              {showExtended ? (
                <div className="max-lg:contents lg:flex lg:flex-col lg:items-end lg:gap-6 lg:[grid-area:stack] w-full min-w-0">
                  <div className="max-lg:[grid-area:photo] flex justify-center lg:justify-end w-full">
                    <img
                      src={member.image}
                      alt={`${member.name}${member.title ? ` — ${member.title}` : ''}`}
                      className="w-full max-w-sm sm:max-w-md rounded-lg shadow-lg object-cover aspect-[3/4] object-top"
                    />
                  </div>
                  <div className="max-lg:[grid-area:creds] flex justify-center lg:justify-end w-full min-w-0">
                    <div className="w-full max-w-sm sm:max-w-md">
                      <Accordion
                        type="multiple"
                        className="space-y-3 sm:space-y-4"
                      >
                        {credentialAccordions.map((section) => (
                          <AccordionItem
                            key={section.id}
                            value={section.id}
                            className="rounded-lg border border-border bg-card px-4 shadow-sm sm:px-6"
                          >
                            <AccordionTrigger className="min-h-[48px] py-3 text-left hover:no-underline sm:py-4">
                              <h2 className="pr-2 text-lg font-bold text-primary-darkGreen sm:text-xl">
                                {section.title}
                              </h2>
                            </AccordionTrigger>
                            <AccordionContent forceMount>
                              <ul className="space-y-2 sm:space-y-3">
                                {section.items.map((item, index) => (
                                  <li key={index} className="flex items-start">
                                    <div className="mt-2 mr-2 h-2 w-2 flex-shrink-0 rounded-full bg-primary-darkGreen sm:mr-3" />
                                    <span
                                      className={`text-sm text-gray-700 sm:text-base${
                                        section.preLine ? ' whitespace-pre-line' : ''
                                      }`}
                                    >
                                      {item}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="max-lg:[grid-area:photo] lg:[grid-area:stack] flex justify-center lg:justify-end w-full">
                  <img
                    src={member.image}
                    alt={`${member.name}${member.title ? ` — ${member.title}` : ''}`}
                    className="w-full max-w-sm sm:max-w-md rounded-lg shadow-lg object-cover aspect-[3/4] object-top"
                  />
                </div>
              )}

              <div className="[grid-area:bio] min-w-0">
                <div>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-primary-darkGreen mb-3 sm:mb-4">
                    {member.name}
                  </h1>
                  {member.title ? (
                    <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8">{member.title}</p>
                  ) : null}
                  <div className="text-base sm:text-lg text-gray-700 leading-relaxed space-y-3 sm:space-y-4">
                    {bioParagraphs.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default TeamDetail
